import {decode} from 'he'
import {http} from '../http'
import * as coreMicroposts from '../../coreApi/microposts'
import {APIResponse} from '../types'
import {ResourceID, Micropost} from '../../../types'
import {EmptyResultError} from '../../../customErrors'
import {handleError} from '../handleError'
import {micropostSeasonFrom} from './micropostSeason'

interface Params extends Micropost {
  clientID: ResourceID
  personID: ResourceID
}

type ResolvedReturn = APIResponse<Micropost>

export interface CreateMicropost {
  (params: Params): Promise<ResolvedReturn>
}

const ERROR_PATH = 'api.createMicropost()'
const GENERIC_ERROR = 'Something that went wrong. Please try again.'

export const createMicropost: CreateMicropost = async ({
  clientID,
  content,
  hyperlinkText,
  hyperlinkURL,
  imageURL,
  isSeasonScoped,
  notify,
  personID,
  seasonID,
  segmentID,
  videoURL,
}) => {
  let resolvedReturn: ResolvedReturn

  try {
    // One expression drives both fields so the request can never claim a scope
    // with no season to scope to — `CreateRequestValidateCustom` rejects that
    // outright, and a client with no configured season would otherwise send it
    // straight from the compose defaults.
    const scopedSeasonID =
      isSeasonScoped && seasonID && seasonID > 0 ? seasonID : null

    const rawParamMap = {
      ClientID: parseInt(clientID, 10),
      Content: content,
      CreatedBy: parseInt(personID, 10),
      ImageURL: imageURL && imageURL.length ? imageURL : null,
      // Sent explicitly rather than left for the service to infer. The service
      // falls back to "SeasonID present means scoped" only for clients that
      // predate the field; now that every create carries a posting season,
      // relying on that fallback would snapshot an all-years post to one
      // season's attendees.
      IsSeasonScoped: scopedSeasonID !== null,
      Notify: notify,
      PublishDate: new Date(Date.now()).toISOString(),
      // Null on an all-years post: the service then stamps the current season
      // as the posting season, which is what All Years means.
      SeasonID: scopedSeasonID,
      SegmentID: segmentID ? parseInt(segmentID, 10) : null,
      TargetText: hyperlinkText && hyperlinkText.length ? hyperlinkText : null,
      TargetURL: hyperlinkURL && hyperlinkURL.length ? hyperlinkURL : null,
      VideoURL: videoURL && videoURL.length ? videoURL : null,
    }

    const response = await coreMicroposts.create(http, rawParamMap)

    if (response.data.Status !== 'Succeeded') {
      if (response.data.Status === 'Failed - No Recipients') {
        throw new EmptyResultError('Unsuccessful Micropost Create: No Results')
      }

      throw new Error('Unsuccessful Micropost Create')
    }

    resolvedReturn = {
      result: 'success',
      data: {
        ID: response.data.Micropost.ID.toString(),
        content: decode(response.data.Micropost.Content || ''),
        videoURL: decode(response.data.Micropost.VideoURL || ''),
        hyperlinkURL: decode(response.data.Micropost.TargetURL || ''),
        hyperlinkText: decode(response.data.Micropost.TargetText || ''),
        imageURL: decode(response.data.Micropost.ImageURL || ''),
        publishDate: new Date(response.data.Micropost.PublishDate),
        notify: response.data.Micropost.Notify,
        segmentID: response.data.Micropost.SegmentID
          ? response.data.Micropost.SegmentID.toString()
          : null,
        ...micropostSeasonFrom(response.data.Micropost),
      },
    }
  } catch (e) {
    const isEmptyResult = e instanceof EmptyResultError

    const logErrorPath = isEmptyResult
      ? `${ERROR_PATH}::NoRecipients`
      : ERROR_PATH

    const userMessage = isEmptyResult
      ? 'No recipients for the Custom Audience'
      : GENERIC_ERROR

    resolvedReturn = handleError(logErrorPath, userMessage, e)
  }

  return Promise.resolve(resolvedReturn)
}
